import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Player, Tactics } from '../../domain/entities';
import { View } from '../../App';
import { PlayerFace } from './PlayerFace';
import { soundService } from '../../services/SoundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance } from '@fortawesome/free-solid-svg-icons';
import './SquadDepthChart.css';

const MIN_SKILL = 40;
const MAX_SKILL = 95;
const SKILL_RANGE = MAX_SKILL - MIN_SKILL;

interface PlayerBlockProps {
    player: Player;
    top: number;
    left: number;
    isStarter: boolean;
    onClick: (player: Player) => void;
    teamColor?: string;
}

const PlayerBlock: React.FC<PlayerBlockProps> = ({ player, top, left, isStarter, onClick, teamColor }) => {
    const isSevereInjury = player.injury?.type === 'Severe';
    const isLightInjury = player.injury?.type === 'Light';

    const blockClasses = `player-block ${isStarter ? 'is-starter' : ''} ${isSevereInjury ? 'severe-injury' : ''} ${isLightInjury ? 'light-injury' : ''}`;
    const injuryTitle = player.injury ? `${player.injury.type} Injury (${player.injury.duration} wks remaining)` : '';
    const starterTitle = isStarter ? 'In Starting XI' : 'Reserve';

    return (
        <div
            className={blockClasses}
            style={{ top: `${top}px`, left: `${left}px` }}
            onClick={() => !isSevereInjury && onClick(player)}
            title={`${player.name} - ${starterTitle}${injuryTitle ? `\n${injuryTitle}` : ''}`}
        >
            <div className="player-block-content">
                <PlayerFace player={player} teamColor={teamColor} />
                <div className="player-block-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span className="player-block-name">{player.name}</span>
                        {player.isListed && <span style={{ fontSize: '0.65em', fontWeight: 'bold', color: 'var(--warning-color)', border: '1px solid var(--warning-color)', padding: '1px 3px', borderRadius: '2px' }}>Listed</span>}
                    </div>
                    <span className="player-block-skill">
                        SKL: {player.skill} {player.form !== 0 && `(${player.form > 0 ? '+' : ''}${player.form})`}
                    </span>
                </div>
            </div>
            {player.injury && <div className="player-block-injury-icon negative"><FontAwesomeIcon icon={faAmbulance} /></div>}
            {isStarter && <div className="player-block-starter-icon">★</div>}
        </div>
    );
};


interface PositionDepthRowProps {
    title: string;
    players: Player[];
    onPlayerClick: (player: Player) => void;
    starters: Set<number>;
    teamColor?: string;
}

const PositionDepthRow: React.FC<PositionDepthRowProps> = ({ title, players, onPlayerClick, starters, teamColor }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [positions, setPositions] = useState<{ player: Player; top: number; left: number }[]>([]);
    const [containerHeight, setContainerHeight] = useState(50);
    
    const averageSkill = useMemo(() => {
        if (players.length === 0) return 0;
        return players.reduce((sum, p) => sum + p.skill, 0) / players.length;
    }, [players]);
    
    const avgLeftPercent = ((averageSkill - MIN_SKILL) / SKILL_RANGE) * 100;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const calculatePositions = () => {
            const containerWidth = container.offsetWidth;
            if (containerWidth === 0) {
                setPositions([]);
                setContainerHeight(50);
                return;
            }

            const PLAYER_BLOCK_WIDTH = 100;
            const PLAYER_BLOCK_HEIGHT = 30;
            const VERTICAL_GAP = 4;

            const placedRects: { x1: number; x2: number; y1: number; y2: number }[] = [];
            const newPositions: { player: Player; top: number; left: number }[] = [];

            const sortedPlayers = [...players].sort((a, b) => a.skill - b.skill);

            for (const player of sortedPlayers) {
                const skillPercent = (player.skill - MIN_SKILL) / SKILL_RANGE;
                const idealLeft = skillPercent * containerWidth;
                const left = Math.max(0, Math.min(idealLeft, containerWidth - PLAYER_BLOCK_WIDTH));

                let top = 0;
                while (true) {
                    const currentRect = {
                        x1: left,
                        x2: left + PLAYER_BLOCK_WIDTH,
                        y1: top,
                        y2: top + PLAYER_BLOCK_HEIGHT,
                    };

                    const isOverlapping = placedRects.some(placed => 
                        currentRect.x1 < placed.x2 && 
                        currentRect.x2 > placed.x1 &&
                        currentRect.y1 < placed.y2 && 
                        currentRect.y2 > placed.y1
                    );

                    if (!isOverlapping) {
                        placedRects.push(currentRect);
                        newPositions.push({ player, top, left });
                        break;
                    }
                    
                    // Find the max bottom of all horizontally overlapping rects to determine the new top
                    const overlappingRects = placedRects.filter(placed => currentRect.x1 < placed.x2 && currentRect.x2 > placed.x1);
                    top = Math.max(...overlappingRects.map(r => r.y2)) + VERTICAL_GAP;
                }
            }
            
            const maxHeight = placedRects.length > 0 ? Math.max(...placedRects.map(r => r.y2)) : 0;
            setContainerHeight(maxHeight > 0 ? maxHeight + VERTICAL_GAP : 50);
            setPositions(newPositions);
        };

        const resizeObserver = new ResizeObserver(calculatePositions);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [players]);

    if (players.length === 0) return null;

    return (
        <div className="depth-chart-row">
            <h4>{title}</h4>
            <div className="depth-chart-axis-container">
                <div className="depth-chart-axis">
                    {Array.from({ length: Math.floor(SKILL_RANGE / 10) + 1 }, (_, i) => {
                        const skillValue = MIN_SKILL + i * 10;
                        return <div key={skillValue} className="axis-label" style={{ left: `${((i * 10) / SKILL_RANGE) * 100}%` }}>{skillValue}</div>;
                    })}
                </div>
                {averageSkill > 0 && (
                    <div 
                        className="average-skill-line" 
                        style={{ left: `clamp(1%, ${avgLeftPercent}%, 99%)` }}
                        title={`Average Skill for this position: ${averageSkill.toFixed(1)}`}
                    />
                )}
                <div 
                    className="player-blocks-container" 
                    ref={containerRef}
                    style={{ height: `${containerHeight}px` }}
                >
                    {positions.map(({ player, top, left }) => (
                        <PlayerBlock key={player.id} player={player} top={top} left={left} onClick={onPlayerClick} isStarter={starters.has(player.id)} teamColor={teamColor} />
                    ))}
                </div>
            </div>
        </div>
    );
};

interface SquadDepthChartProps {
    squad: Player[];
    tactics: Tactics;
    pushView: (view: View) => void;
    teamColor?: string;
}

export const SquadDepthChart: React.FC<SquadDepthChartProps> = ({ squad, tactics, pushView, teamColor }) => {

    const playersByPosition = {
        GK: squad.filter(p => p.position === 'GK').sort((a,b) => b.skill - a.skill),
        DEF: squad.filter(p => p.position === 'DEF').sort((a,b) => b.skill - a.skill),
        MID: squad.filter(p => p.position === 'MID').sort((a,b) => b.skill - a.skill),
        FWD: squad.filter(p => p.position === 'FWD').sort((a,b) => b.skill - a.skill),
    };
    
    const starterIds = useMemo(() => new Set(Object.values(tactics.lineup).filter(id => id !== null)), [tactics.lineup]);

    const handlePlayerClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    return (
        <div className="panel-content squad-depth-chart-container">
            <PositionDepthRow title="Goalkeepers" players={playersByPosition.GK} onPlayerClick={handlePlayerClick} starters={starterIds} teamColor={teamColor} />
            <PositionDepthRow title="Defenders" players={playersByPosition.DEF} onPlayerClick={handlePlayerClick} starters={starterIds} teamColor={teamColor} />
            <PositionDepthRow title="Midfielders" players={playersByPosition.MID} onPlayerClick={handlePlayerClick} starters={starterIds} teamColor={teamColor} />
            <PositionDepthRow title="Forwards" players={playersByPosition.FWD} onPlayerClick={handlePlayerClick} starters={starterIds} teamColor={teamColor} />
        </div>
    );
};